import React, { useEffect, useCallback } from "react";
import "./App.css";
import AppRoutes from "./Components/AppRoutes/AppRoutes";
import LoadCSSforfirstTime from "./CloudStorage/LoadCSSforfirstTime";
import HelmetAllInOne from "./Components/Seo/HelmetAllInOne";
import "react-toastify/dist/ReactToastify.css";
import { RouteBaseStateProvider } from "./Context/RouteBaseStateContext";
import { AllFilterBaseProvider } from "./Context/AllFilterBaseContext";
import { PaginationProvider } from "./Context/ChatPaginationContext";

const APP_SCSS_PATH = "Assets/scss/app.module.scss";
const ACTIVATION_KEYS = new Set(["Enter", " "]);
const TYPING_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);
const INTERACTIVE_CHILD_SELECTOR = 'a, button, [role="button"], [tabindex="0"]';
const KEYBOARD_NAVIGATION_SELECTOR = `
  [data-keyboard-navigation-managed="true"],
  li[onclick],
  li[role="button"],
  li:has(> a:only-child):not(:has(button, input, textarea, select)),
  a:not([href]):not([tabindex]),
  button:not([tabindex]),
  [role="button"]:not([tabindex])
`;
const SEARCH_INPUT_SELECTOR =
  'input[type="search"], input[placeholder*="Search"], .search-input';

const getCurrentLanguage = () => localStorage.getItem("laung") || "en";

const isTypingElement = (element) =>
  TYPING_TAGS.has(element?.tagName) || element?.isContentEditable;

const isKeyboardInteractiveElement = (element) =>
  element?.tagName === "LI" ||
  (element?.tagName === "A" && !element.getAttribute("href")) ||
  element?.tagName === "BUTTON" ||
  element?.getAttribute("role") === "button" ||
  element?.getAttribute("tabindex") === "0";

const clickActiveElement = (element) => {
  if (element.tagName === "LI") {
    const firstInteractive = element.querySelector(INTERACTIVE_CHILD_SELECTOR);
    (firstInteractive || element).click();
    return;
  }

  if (
    element.tagName === "A" ||
    element.tagName === "BUTTON" ||
    element.getAttribute("role") === "button" ||
    element.getAttribute("tabindex") === "0"
  ) {
    element.click();
  }
};

const focusSearchInput = () => {
  const searchInput = document.querySelector(SEARCH_INPUT_SELECTOR);
  searchInput?.focus();
};

const closeActiveOverlay = () => {
  const activeModal = document.querySelector(".modal.show, .popupmodal.show");
  const activeSidebar = document.querySelector(
    ".dashboard-sidebar.show, .sidebar.show"
  );
  const activeDropdown = document.querySelector(".dropdown.show, .menu.open");

  if (activeModal) {
    activeModal
      .querySelector('[data-dismiss="modal"], .close-button, .btn-close')
      ?.click();
    return;
  }

  if (activeSidebar) {
    document.dispatchEvent(new CustomEvent("closeMobileSidebar"));
    return;
  }

  activeDropdown?.querySelector(".dropdown-toggle, .menu-toggle")?.click();
};

const createKeyboardNavigationCleanup = () => {
  const interactiveElements = document.querySelectorAll(
    KEYBOARD_NAVIGATION_SELECTOR
  );
  const cleanupCallbacks = [];

  interactiveElements.forEach((element) => {
    if (!element.hasAttribute("tabindex")) {
      element.setAttribute("tabindex", "0");
    }
    element.dataset.keyboardNavigationManaged = "true";

    const handleKeyPress = (event) => {
      if (ACTIVATION_KEYS.has(event.key)) {
        event.preventDefault();
        element.click();
      }
    };
    const handleFocus = () => element.classList.add("keyboard-focused");
    const handleBlur = () => element.classList.remove("keyboard-focused");

    element.addEventListener("keydown", handleKeyPress);
    element.addEventListener("focus", handleFocus);
    element.addEventListener("blur", handleBlur);

    cleanupCallbacks.push(() => {
      element.removeEventListener("keydown", handleKeyPress);
      element.removeEventListener("focus", handleFocus);
      element.removeEventListener("blur", handleBlur);
    });
  });

  return () => cleanupCallbacks.forEach((cleanup) => cleanup());
};

export default function App() {
  const handleKeyboardNavigation = useCallback((event) => {
    const activeElement = document.activeElement;

    if (
      isTypingElement(activeElement) ||
      !ACTIVATION_KEYS.has(event.key) ||
      !isKeyboardInteractiveElement(activeElement)
    ) {
      return;
    }

    event.preventDefault();
    clickActiveElement(activeElement);
  }, []);

  const handleGlobalKeyDown = useCallback((event) => {
    if (isTypingElement(event.target)) return;

    handleKeyboardNavigation(event);

    switch (event.key) {
      case "/":
        event.preventDefault();
        focusSearchInput();
        break;

      case "?":
        event.preventDefault();
        console.log("Show keyboard shortcuts help");
        break;

      case "Escape":
        closeActiveOverlay();
        break;

      case "d":
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          window.location.href = `/${getCurrentLanguage()}/dashboard`;
        }
        break;

      case "h":
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          window.location.href = `/${getCurrentLanguage()}`;
        }
        break;

      case "p":
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          window.location.href = `/${getCurrentLanguage()}/dashboard/manageprofile`;
        }
        break;

      default:
        break;
    }
  }, [handleKeyboardNavigation]);

  useEffect(() => {
    LoadCSSforfirstTime(APP_SCSS_PATH);

    window.addEventListener("keydown", handleGlobalKeyDown);

    let cleanupKeyboardNavigation = createKeyboardNavigationCleanup();
    const observer = new MutationObserver(() => {
      cleanupKeyboardNavigation();
      cleanupKeyboardNavigation = createKeyboardNavigationCleanup();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown);
      cleanupKeyboardNavigation();
      observer.disconnect();
    };
  }, [handleGlobalKeyDown]);

  return (
    <div className="keyboard-navigation-enabled">
      <RouteBaseStateProvider>
        <AllFilterBaseProvider>
          <HelmetAllInOne />
          <PaginationProvider>
            <AppRoutes />
          </PaginationProvider>
        </AllFilterBaseProvider>
      </RouteBaseStateProvider>
    </div>
  );
}
